import { action, computed, makeObservable, observable } from 'mobx';
import { UserActivityCategory, UserActivityTypes } from '@pascal/iframe-service';

import { RootStore } from '.';
import { HttpResponse, sidebarMenuController } from '../api';
import { generateSidebarIconUrl, isMobile } from '../helpers';
import { LaunchType, NotificationPriority, SideBarNavigationKey, SidebarOpenSource, SideMenuIcons } from '../enums';
import { SidebarIcon } from '../models';
import { T } from '../constants';
import { SideBarNotificationNames } from '../enums/sidebar-notification-name';

export class SideBarMenuStore {
  private rootStore: RootStore;

  isSidebarOpen = false;

  /**
   * @description the selected sidebar tab; null until the sidebar mounts and picks its default tab
   */
  activeTab: SideBarNavigationKey | null = null;

  // when the sidebar last opened, for the duration in the Sidebar Close event
  private openedAt = 0;

  /**
   * @description promotions nav tab badge: set when a promotion-related sidebar notification is enqueued,
   * cleared the next time the promotions tab is opened
   */
  hasPromotionsNotification = false;

  /**
   * @description winners nav tab badge: set when a big-win sidebar notification is enqueued,
   * cleared the next time the winners tab is opened
   */
  hasTopWinsNotification = false;

  newUnseenGames: Array<number> = [];
  newUnseenTournaments: Array<number> = [];

  public newGames = new HttpResponse<Array<number>>([]);

  public newTournaments = new HttpResponse<Array<number>>([]);

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {

      showSidebar: computed,

      showPascalPromotions: computed,

      isLobbyOnly: computed,

      lobbyNotifCount: computed,

      tournamentNotifCount: computed,

      activeSidebarIcons: computed,

      newTournaments: observable,

      newGames: observable,

      newUnseenGames: observable,

      newUnseenTournaments: observable,

      isSidebarOpen: observable,
      setIsSidebarOpen: action.bound,
      openSidebar: action.bound,

      activeTab: observable,
      setActiveTab: action.bound,

      hasPromotionsNotification: observable,
      setHasPromotionsNotification: action.bound,

      hasTopWinsNotification: observable,
      setHasTopWinsNotification: action.bound,

      fetchNewGames: action.bound,
      fetchNewTournaments: action.bound,

      openPromotionDetails: action.bound,
    });
  }

  /**
   * @description the sidebar exists when at least one of its tabs (lobby, promotions, top winners) has
   * something to show, and never in replay, cashier or monitor launches
   */
  public get showSidebar(): boolean {
    const { gameStore } = this.rootStore;
    const { data } = gameStore.launchData;
    if (!data) return false;
    const { launchType } = data;
    if (launchType === LaunchType.Replay || launchType === LaunchType.Cashier || launchType === LaunchType.Monitor) return false;
    return !!data.hasLobby || this.showPascalPromotions || gameStore.topWinnersEnabled;
  }

  /**
   * @description the promotions tab: any promo tool is on. Jackpot and wheel are fed by hub pushes, so they
   * count only once the hub has connected; a wheel that is triggered or on screen always counts.
   */
  public get showPascalPromotions(): boolean {
    const { gameStore, hubStore, boostedJackpotStore, freeSpinStore } = this.rootStore;
    const { data } = gameStore.launchData;
    if (!data) return false;
    const { everConnected: hubReady } = hubStore;
    const hasJackpot = !!data.hasJackpot && hubReady;
    const hasPGWheel = !!boostedJackpotStore.wheelInfo && (hubReady || boostedJackpotStore.isWheelInFlight);
    const hasFreebet = !!freeSpinStore.freeSpinInfo?.length;
    return hasJackpot || !!data.promoInfo || !!data.hasPreLobby || hasPGWheel || !!data.hasTournament || hasFreebet;
  }

  /**
   * @description lobby is the only tab the sidebar would show
   */
  public get isLobbyOnly(): boolean {
    const { gameStore } = this.rootStore;
    return !!gameStore.launchData.data?.hasLobby && !this.showPascalPromotions && !gameStore.topWinnersEnabled;
  }

  public get tournamentNotifCount(): number {
    return this.newUnseenTournaments.length
  }

  public get lobbyNotifCount(): number {
    return this.newUnseenGames.length
  }

  public get notifCount(): number {
    return this.newUnseenGames.length + this.newUnseenTournaments.length
  }

  public get activeSidebarIcons(): Array<SidebarIcon> {
    const { launchData: { data } } = this.rootStore.gameStore;
    if (!data) return [];
    const result: Array<SidebarIcon> = [];

    const hasPromo = data.promoInfo;
    const hasPGWheel = data.wheelInfo;
    const hasTournament = data.hasTournament;
    const hasJackpot = data.hasJackpot;
    const hasFreebet = !!data.awardedFreeBets?.length;
    const hasTopWinners = this.rootStore.gameStore.topWinnersEnabled;

    result.push({ type: SideMenuIcons.Default, url: generateSidebarIconUrl(SideMenuIcons.Default) });
    if (hasPromo) result.push({ type: SideMenuIcons.Promo, url: generateSidebarIconUrl(SideMenuIcons.Promo) });
    if (hasPGWheel) result.push({ type: SideMenuIcons.Wheel, url: generateSidebarIconUrl(SideMenuIcons.Wheel) });
    if (hasTournament) result.push({ type: SideMenuIcons.Tournament, url: generateSidebarIconUrl(SideMenuIcons.Tournament) });
    if (hasJackpot) result.push({ type: SideMenuIcons.Jackpot, url: generateSidebarIconUrl(SideMenuIcons.Jackpot) });
    if (hasFreebet) result.push({ type: SideMenuIcons.Freespin, url: generateSidebarIconUrl(SideMenuIcons.Freespin) });
    return result;
  }

  // Every open and close goes through here, so each is tracked exactly once, whatever triggered it.
  // `source` says what opened the sidebar; setting the current value again is not tracked.
  setIsSidebarOpen(value: boolean, source?: SidebarOpenSource) {
    if (value !== this.isSidebarOpen) this.trackSidebarToggle(value, source);
    this.isSidebarOpen = value;
  }

  // What the sidebar openers (the launcher's widget and the game's own button) do. On web, when lobby is the
  // only tab, the lobby modal opens instead of the sidebar, the same as clicking the Lobby tab there would.
  public openSidebar(source: SidebarOpenSource): void {
    if (!isMobile() && this.isLobbyOnly) {
      this.rootStore.lobbyStore.setIsLobbyOpen(true);
      return;
    }
    this.setIsSidebarOpen(true, source);
  }

  setActiveTab(value: SideBarNavigationKey) {
    this.activeTab = value;
  }

  // A tab the player clicked, including the desktop Lobby tab, which opens the lobby frame instead.
  public trackTabClick(tab: SideBarNavigationKey): void {
    this.rootStore.userActivityStore.sendTrackUserActivity({
      category: UserActivityCategory.PromoTools,
      type: UserActivityTypes.SidebarTab,
      data: { tab }
    });
  }

  private trackSidebarToggle(open: boolean, source?: SidebarOpenSource): void {
    const data: Record<string, string | number> = {};
    if (this.activeTab) data.tab = this.activeTab;
    if (open) {
      this.openedAt = Date.now();
      if (source) data.source = source;
    } else {
      data.durationMs = Date.now() - this.openedAt;
    }
    this.rootStore.userActivityStore.sendTrackUserActivity({
      category: UserActivityCategory.PromoTools,
      type: open ? UserActivityTypes.SidebarOpen : UserActivityTypes.SidebarClose,
      data
    });
  }

  setHasPromotionsNotification(value: boolean) {
    this.hasPromotionsNotification = value;
  }

  setHasTopWinsNotification(value: boolean) {
    if (value && !this.rootStore.gameStore.topWinnersEnabled) return;
    this.hasTopWinsNotification = value;
  }

  // Enqueues the one-time active-promos announcement. Called from the launch-data fan-out,
  // before any component or socket event can enqueue, so it is guaranteed to be the first
  // item of the sidebar notification queue; the paused queue holds it until the opener mounts.
  public announceActivePromos(): void {
    const hasPromos = this.activeSidebarIcons.some(({ type }) => type !== SideMenuIcons.Default);
    if (!hasPromos) return;
    this.rootStore.sidebarNotificationStore.enqueue('', SideMenuIcons.ActivePromos, NotificationPriority.High);
  }

  // Opens the detailed view of the promotion a sidebar icon/notification stands for.
  // Each branch mirrors the `onDetailsClick` of the matching sidebar card.
  public openPromotionDetails(type: SideMenuIcons = SideMenuIcons.Default): boolean {
    const {
      lobbyStore, promoStore, jackpotStore, freeSpinStore,
      tournamentStore, boostedJackpotStore, gameStore, userActivityStore
    } = this.rootStore;
    userActivityStore.sendTrackUserActivity({
      category: UserActivityCategory.PromoTools,
      type: UserActivityTypes.SidebarNotification,
      data: SideBarNotificationNames[type]
    });
    switch (type) {
      case SideMenuIcons.Tournament:
        this.markTournamentsAsSeen();
        tournamentStore.openTournamentFrame(gameStore.launchData.data?.tournamentInfo?.id);
        break;
      case SideMenuIcons.Jackpot:
        jackpotStore.setJackpotDetailsOpen(true);
        break;
      case SideMenuIcons.Wheel:
        boostedJackpotStore.setIsBoostedJackpotInfoPopup(true);
        break;
      case SideMenuIcons.Promo:
        promoStore.setPromoModalOpen(true);
        break;
      case SideMenuIcons.Freespin:
        freeSpinStore.showAllFreeSpinRewards(freeSpinStore.freeSpinInfo);
        break;
      case SideMenuIcons.Win:
        if (!gameStore.topWinnersEnabled) return false;
        gameStore.openBigWinReplay();
        break;
      case SideMenuIcons.Default:
        this.markGamesAsSeen();
        lobbyStore.setIsLobbyOpen(true);
        break;
      default:
        return false;
    }

    this.setIsSidebarOpen(false);
    return true;
  }

  sendSidebarPostMessage() {
    this.rootStore?.postMessageStore.sendSidebarPostMessage(this.notifCount)
  }

  async fetchNewGames(partnerId: number): Promise<void> {
    this.newGames = this.newGames.fetching();
    const data = await sidebarMenuController.getLobbyNewGames(partnerId);
    this.newGames = this.newGames.fetched(data);
    this.checkLobbyUnseenGames();
  }

  public checkLobbyUnseenGames(): void {
    if (this.newGames.data?.length) {
      const lastNewGames = JSON.parse(localStorage.getItem('lastNewGames') || '[]');
      this.newUnseenGames = this.newGames.data.filter((id) => !lastNewGames.includes(id));
      if (this.newUnseenGames.length) {
        setTimeout(() => {
          this.rootStore.sidebarNotificationStore.enqueue(
            `${this.rootStore.translationsStore.translate(T.NEW_GAME_ADDED_TO_LOBBY)}`,
            SideMenuIcons.Default,
          );
        }, 30_000);
      }
    }
  }

  public async fetchNewTournaments(): Promise<void> {
    this.newTournaments = this.newTournaments.fetching();
    const data = await sidebarMenuController.getNewTournaments();
    this.newTournaments = this.newTournaments.fetched(data);
    this.checkTournamentUnseenGames()
  }
  public checkTournamentUnseenGames(): void {
    if (this.newTournaments.data?.length) {
      const lastNewTournaments = JSON.parse(localStorage.getItem('lastNewTournaments') || '[]');
      this.newUnseenTournaments = this.newTournaments.data.filter((id) => !lastNewTournaments.includes(id));
    }
  }
  public markGamesAsSeen() {
    localStorage.setItem('lastNewGames', JSON.stringify(this.newGames?.data || []));
    this.newUnseenGames = [];
    this.sendSidebarPostMessage();
  }

  public markTournamentsAsSeen() {
    localStorage.setItem('lastNewTournaments', JSON.stringify(this.newTournaments?.data || []));
    this.newUnseenTournaments = [];
    this.sendSidebarPostMessage();
  }
}
